import { useEffect, useMemo, useRef, useState } from "react";

import {
  SectionItem,
  Toast,
  useToast,
} from "@smart/bridge-intake-components-dom";
import {
  assignGeneratedSectionItemOrders,
  FormCategory,
  GeneratedFormSchema,
} from "@smart/bridge-types-basic";
import { useRefresh } from "@smart/itops-hooks-dom";
import {
  jsonParse,
  logError,
  rankBetween,
  sortField,
} from "@smart/itops-utils-basic";
import {
  useAddFormMatterType,
  useLoadStaffDetails,
  useQueryMatterFields,
  useRemoveFormMatterType,
  useToggleSlug,
  useTranslateForm,
  useUpdateForm,
  useUpdateFormPublishStatus,
} from "@smart/manage-gql-client-dom";
import { useUser } from "@smart/manage-hooks-dom";

import { EditView } from "./edit-view";
import { FormHeading } from "./heading";
import { Preview } from "./preview";
import { PublishForm } from "./publish-form";
import { PublishProvider } from "./publish-form/context";
import { FormInfo, GqlSubmissionLinkHash, UpdateAIFillSettings } from "./types";
import { WebEnquiriesTab } from "./web-enquiries";
import {
  LanguageKey,
  SupportedLanguageOption,
  baseLanguageKey,
  defaultSupportedLanguages,
  getLanguageLabel,
  FormBuilderTabOptions,
} from "../../types";
import { FormLoadingStatus } from "../create-form/creation-status";
import { EditingFormInfo } from "../list-forms/form";
import { DeactivateFormModal } from "../shared/deactivate-form-modal";
import {
  GqlField,
  GqlForm,
  GqlGroup,
  GqlMatterLayout,
  GqlMatterType,
  GqlSection,
  GqlSetting,
  GqlTeam,
} from "../types";

type ViewProps = {
  tab?: FormBuilderTabOptions;
  fields?: GqlField[] | null;
  formUri: string;
  creationStatus?: string | null;
  sections?: GqlSection[] | null;
  team?: GqlTeam | null;
  setting?: GqlSetting | null;
  form?: GqlForm | null;
  groups?: GqlGroup[] | null;
  loading: {
    team: boolean;
    form: boolean;
    sections: boolean;
    groups: boolean;
    fields: boolean;
    matterTypes: boolean;
    matterLayouts: boolean;
    submissionLinkHashes: boolean;
  };
  previewOptions: {
    previewErrorToggle: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
    previewResponses: [
      Record<string, any>,
      React.Dispatch<React.SetStateAction<Record<string, any>>>,
    ];
    previewLastSection: [
      SectionItem | "summary" | undefined,
      React.Dispatch<React.SetStateAction<SectionItem | "summary" | undefined>>,
    ];
  };
  loadingStatus: FormLoadingStatus;
  matterTypes?: GqlMatterType[];
  matterLayouts?: GqlMatterLayout[];
  loadMatterFields: ReturnType<typeof useQueryMatterFields>;
  createForm: () => Promise<void>;
  updateAIFillSettings: UpdateAIFillSettings;
  canUpdate: boolean;
  setTab: React.Dispatch<React.SetStateAction<FormBuilderTabOptions>>;
  teamForms?: FormInfo[];
  editFormLastSectionUri: [
    string | undefined,
    React.Dispatch<React.SetStateAction<string | undefined>>,
  ];
  staffDetails: ReturnType<typeof useLoadStaffDetails>;
  intakeFormKey: string;
  setToasts?: (toasts: Toast[]) => void;
  viewingLanguage: LanguageKey;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
  toggleFormActive: () => Promise<void>;
};

const buildQuestionsFromAIFeedback = (
  feedback?: GqlForm["aiUserFeedback"] | null,
  creationStatus?: string | null,
) =>
  useMemo(() => {
    if (
      feedback &&
      (feedback.status === "generating" ||
        feedback.status === "generated" ||
        (feedback.status === "completed" && !!creationStatus)) &&
      feedback.output
    ) {
      try {
        const updatedAt = new Date().toISOString();
        const generated = GeneratedFormSchema.parse(
          jsonParse(feedback.output, "parse generated output"),
        );

        let previousSection: string | undefined;
        const sections: GqlSection[] = [];
        const fields: GqlField[] = [];
        const groups: GqlGroup[] = [];
        for (const section of sortField(generated.sections, {
          key: "index",
          dir: "asc",
        })) {
          const sectionOrder = rankBetween({ after: previousSection });
          previousSection = sectionOrder;

          sections.push({
            uri: section.uri,
            __typename: "Section",
            values: {
              uri: section.uri,
              formUri: feedback.formUri!,
              title: section.displayTitle || section.title,
              order: sectionOrder,
              updatedAt,
              operationId: "",
              __typename: "SectionValues",
            },
          });

          const { groupOrder, questionOrder } =
            assignGeneratedSectionItemOrders(section.questions || []);

          for (const group of section.groups || []) {
            const groupItemOrder = groupOrder.get(group.uri);
            if (groupItemOrder) {
              groups.push({
                uri: group.uri,
                __typename: "Group",
                values: {
                  uri: group.uri,
                  sectionUri: section.uri,
                  formUri: feedback.formUri!,
                  order: groupItemOrder,
                  label: group.label,
                  type: group.type,
                  updatedAt,
                  operationId: "",
                  layout: {
                    __typename: "MatterLayout",
                    ...group.layout,
                  },
                  field: {
                    __typename: "MatterField",
                    ...group.field,
                  },
                  __typename: "GroupValues",
                },
              });
            }
          }

          for (const field of sortField(section.questions || [], {
            key: "index",
            dir: "asc",
          })) {
            fields.push({
              uri: field.uri,
              __typename: "Field",
              values: {
                uri: field.uri,
                sectionUri: section.uri,
                formUri: feedback.formUri!,
                groupUri: field.groupUri,
                order: questionOrder.get(field.uri)!,
                label: field.label,
                type: field.type,
                options:
                  field.options?.map((o) => ({
                    __typename: "FieldOption",
                    label: o,
                    value: o,
                  })) || [],
                hint: "",
                mandatory: false,
                updatedAt,
                operationId: "",
                layout: field.layout && {
                  __typename: "MatterLayout",
                  ...field.layout,
                },
                field: field.field && {
                  __typename: "MatterField",
                  ...field.field,
                },
                __typename: "FieldValues",
              },
            });
          }
        }

        return {
          sections,
          fields,
          groups,
        };
      } catch (e) {
        logError(e, feedback);
      }
    }

    return undefined;
  }, [creationStatus, feedback?.status, feedback?.output]);

const View = ({
  tab,
  fields,
  formUri,
  creationStatus,
  sections,
  team,
  setting,
  form,
  groups,
  loading,
  previewOptions,
  loadingStatus,
  matterTypes,
  matterLayouts,
  loadMatterFields,
  createForm,
  updateAIFillSettings,
  canUpdate,
  setTab,
  teamForms,
  editFormLastSectionUri,
  staffDetails,
  intakeFormKey,
  setToasts,
  viewingLanguage,
  submissionLinkHashes,
  toggleFormActive,
}: ViewProps) => {
  const feedbackQuestions = buildQuestionsFromAIFeedback(
    form?.aiUserFeedback,
    creationStatus,
  );
  const questions = feedbackQuestions || {
    sections,
    groups,
    fields,
  };

  switch (tab) {
    case "questions":
      return (
        <EditView
          formUri={formUri}
          form={form}
          loading={loading}
          loadingStatus={loadingStatus}
          matterTypes={matterTypes}
          matterLayouts={matterLayouts}
          loadMatterFields={loadMatterFields}
          createForm={createForm}
          updateAIFillSettings={updateAIFillSettings}
          canUpdate={canUpdate}
          setTab={setTab}
          teamForms={teamForms}
          editFormLastSectionUri={editFormLastSectionUri}
          setToasts={setToasts}
          viewingLanguage={viewingLanguage}
          hasSubmissionLinks={!!submissionLinkHashes?.length}
          {...questions}
        />
      );
    case "preview":
      return (
        <div className="flex flex-grow bg-gray-100">
          <Preview
            team={team}
            setting={setting}
            form={form}
            formUri={formUri}
            canUpdate={canUpdate}
            previewOptions={previewOptions}
            previewLoading={
              loading.team ||
              loading.form ||
              loading.sections ||
              loading.groups ||
              loading.fields ||
              loading.submissionLinkHashes
            }
            staffDetails={staffDetails}
            intakeFormKey={intakeFormKey}
            setToasts={setToasts}
            {...questions}
          />
        </div>
      );
    case "share":
      return (
        <WebEnquiriesTab
          form={form}
          submissionLinkHashes={submissionLinkHashes}
          sections={sections}
          matterLayouts={matterLayouts}
          toggleFormActive={toggleFormActive}
        />
      );
    default:
      return null;
  }
};

type EditFormProps = {
  formUri: string;
  team?: GqlTeam | null;
  setting?: GqlSetting | null;
  form?: GqlForm | null;
  sections?: GqlSection[] | null;
  groups?: GqlGroup[] | null;
  fields?: GqlField[] | null;
  title?: string;
  loading: {
    team: boolean;
    form: boolean;
    matterTypes: boolean;
    sections: boolean;
    groups: boolean;
    fields: boolean;
    matterLayouts: boolean;
    submissionLinkHashes: boolean;
  };
  loadingStatus: FormLoadingStatus;
  category: FormCategory | undefined;
  matterTypes?: GqlMatterType[];
  matterLayouts?: GqlMatterLayout[];
  teamForms?: FormInfo[];
  loadMatterFields: ReturnType<typeof useQueryMatterFields>;
  createForm: () => Promise<void>;
  refetchForm: () => Promise<unknown> | void;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
};

const EditFormV2 = ({
  formUri,
  team,
  setting,
  form,
  sections,
  groups,
  fields,
  title,
  loading,
  loadingStatus,
  category,
  matterTypes,
  matterLayouts,
  teamForms,
  loadMatterFields,
  createForm,
  refetchForm,
  submissionLinkHashes,
}: EditFormProps) => {
  const [intakeFormKey, refreshIntakeForm] = useRefresh();
  const previewErrorToggle = useState(false);
  const previewResponses = useState<Record<string, any>>({});
  const previewLastSection = useState<SectionItem | "summary" | undefined>();
  const [viewingLanguage, setViewingLanguage] =
    useState<LanguageKey>(baseLanguageKey);
  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;
  const editFormLastSectionUri = useState<string | undefined>();
  const { permission } = useUser();
  const [updateForm, { loading: updatingForm }] = useUpdateForm();
  const [updateFormPublishStatus] = useUpdateFormPublishStatus();
  const [updateSlug, { loading: updatingSlug }] = useToggleSlug();
  const [translateForm, { loading: translatingForm }] = useTranslateForm();
  const [addFormMatterType] = useAddFormMatterType();
  const [removeFormMatterType] = useRemoveFormMatterType();
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const { setToasts } = useToast();
  const formTranslationPollingTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const formTranslationPollingTokenRef = useRef(0);

  const clearFormTranslationPolling = () => {
    if (!formTranslationPollingTimeoutRef.current) return;
    clearTimeout(formTranslationPollingTimeoutRef.current);
    formTranslationPollingTimeoutRef.current = undefined;
  };

  useEffect(() => () => clearFormTranslationPolling(), []);

  const staffDetails = useLoadStaffDetails({
    staffIds: [
      ...new Set(
        fields?.flatMap((field) => field.values.availableStaffIds ?? []),
      ),
    ],
  });

  const clearIntakeForm = () => {
    previewResponses[1]({});
    previewLastSection[1](undefined);
    refreshIntakeForm();
  };

  const translatedFormTitle = !isViewingBaseLanguage
    ? form?.rawForm.values.translations?.find(
        (translation) => translation.language === viewingLanguage,
      )?.title
    : undefined;

  const formTitle = translatedFormTitle || form?.formTitle || title;
  const isFormActive = !!form?.active;
  const matterTypeNames =
    form?.matterTypeNames || matterTypes?.map((mt) => mt.name);
  const matterTypeLocations =
    form?.matterTypeLocations || matterTypes?.map((mt) => mt.location);
  const isLanguageKey = (value: string): value is LanguageKey =>
    value === baseLanguageKey ||
    defaultSupportedLanguages.some((language) => language.key === value);

  const supportedLanguages = (
    form?.rawForm.values.supportedLanguages ?? []
  ).reduce<SupportedLanguageOption[]>((acc, language) => {
    if (isLanguageKey(language.key)) {
      acc.push({ key: language.key, label: language.label });
    }
    return acc;
  }, []);

  useEffect(() => {
    if (isViewingBaseLanguage) return;
    const hasLanguage = supportedLanguages.some(
      (language) => language.key === viewingLanguage,
    );
    if (!hasLanguage) {
      setViewingLanguage(baseLanguageKey);
    }
  }, [supportedLanguages, viewingLanguage]);

  const canUpdate =
    !form ||
    !permission ||
    permission?.can("update", { type: "form", value: form });

  const [tab, setTab] = useState<FormBuilderTabOptions>(
    canUpdate ? "questions" : "preview",
  );

  let activeTab;
  if ((form || !loading.form) && permission) activeTab = tab;

  const canActivate = permission?.can("update", {
    type: "slug",
    value: {
      uri: form?.rawForm.slug?.uri || "",
      formUri,
      active: form?.rawForm.slug?.values.active ?? false,
    },
    parentForm: {
      uri: formUri,
      teamUri: form?.teamUri || "",
      active: form?.rawForm.slug?.values.active ?? undefined,
    },
  });

  const toggleFormActive = async () => {
    await updateSlug({
      variables: {
        formUri,
        fields: { active: !isFormActive },
      },
    });
  };

  const onActiveToggleClick = async () => {
    if (isFormActive && !!submissionLinkHashes?.length) {
      setDeactivateConfirmOpen(true);
    } else {
      await toggleFormActive();
    }
  };

  const confirmDeactivateForm = async () => {
    await updateSlug({
      variables: {
        formUri,
        fields: { active: false },
      },
    });
    setDeactivateConfirmOpen(false);
  };

  const validateFormTitle = (updatedTitle: string): boolean =>
    !!updatedTitle &&
    !teamForms?.find(
      (f) =>
        f.uri !== formUri &&
        f.formTitle.toLocaleLowerCase() === updatedTitle.toLocaleLowerCase(),
    );

  const startFormTranslationPolling = ({
    baselineTranslations,
    baselineOperationId,
  }: {
    baselineTranslations: string;
    baselineOperationId?: string | null;
  }) => {
    if (!form?.rawForm.values.supportedLanguages?.length) return;

    formTranslationPollingTokenRef.current += 1;
    const pollingToken = formTranslationPollingTokenRef.current;
    clearFormTranslationPolling();

    let attempts = 0;
    const maxAttempts = 10;
    const intervalMs = 3000;

    const poll = async () => {
      if (pollingToken !== formTranslationPollingTokenRef.current) return;

      try {
        const refetched = await Promise.resolve(refetchForm());
        if (pollingToken !== formTranslationPollingTokenRef.current) return;

        const values = (
          refetched as {
            data?: {
              form?: {
                values?: {
                  operationId?: string | null;
                  translations?: unknown;
                } | null;
              } | null;
            };
          }
        )?.data?.form?.values;

        if (!values) {
          clearFormTranslationPolling();
          return;
        }

        const latestTranslations = JSON.stringify(values.translations || []);
        const latestOperationId = values.operationId;
        const hasTranslationsChanged =
          latestTranslations !== baselineTranslations;
        const hasOperationAdvanced =
          !!baselineOperationId &&
          !!latestOperationId &&
          latestOperationId !== baselineOperationId;

        if (hasTranslationsChanged || hasOperationAdvanced) {
          clearFormTranslationPolling();
          return;
        }
      } catch (error) {
        console.error(error);
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        clearFormTranslationPolling();
        return;
      }

      formTranslationPollingTimeoutRef.current = setTimeout(() => {
        poll().catch(console.error);
      }, intervalMs);
    };

    poll().catch(console.error);
  };

  const updateFormTitle = async (updatedTitle: string) => {
    if (!form) await createForm();

    const formCategory = form?.formCategory || category;
    if (form && formCategory) {
      const baselineTranslations = JSON.stringify(
        form.rawForm.values.translations || [],
      );
      const result = await updateForm({
        variables: {
          uri: formUri,
          fields: {
            title: updatedTitle,
            category: formCategory,
            response: form.rawForm.values.response,
          },
          forLanguage: isViewingBaseLanguage ? undefined : viewingLanguage,
        },
      });

      await updateFormPublishStatus({
        variables: { uri: formUri, hasUnpublishedChanges: true },
      });

      if (isViewingBaseLanguage && updatedTitle !== form.formTitle) {
        startFormTranslationPolling({
          baselineTranslations,
          baselineOperationId:
            result?.data?.updateForm?.values.operationId ??
            form.rawForm.values.operationId,
        });
      }
    }
  };

  const updateAIFillSettings: UpdateAIFillSettings = async (
    settings,
    forLanguage,
  ) => {
    if (form && Object.keys(settings).length) {
      const baselineTranslations = JSON.stringify(
        form.rawForm.values.translations || [],
      );
      const hasTranslatableAIFillChange =
        settings.sectionTitle !== undefined ||
        settings.fileUploadLabel !== undefined ||
        settings.fileUploadDescription !== undefined;
      const result = await updateForm({
        variables: {
          uri: formUri,
          fields: {
            title: form.formTitle,
            category: form.formCategory,
            response: form.rawForm.values.response,
            aiFillSettings: settings,
          },
          forLanguage,
        },
      });

      await updateFormPublishStatus({
        variables: { uri: formUri, hasUnpublishedChanges: true },
      });

      if (!forLanguage && hasTranslatableAIFillChange) {
        startFormTranslationPolling({
          baselineTranslations,
          baselineOperationId:
            result?.data?.updateForm?.values.operationId ??
            form.rawForm.values.operationId,
        });
      }
    }
  };

  const saveMessageTemplates = async (messages: {
    new?: string | null;
    reminder?: string | null;
    confirmation?: string;
    confirmationMode?: "message" | "redirect";
    redirectUrl?: string | null;
  }) => {
    if (form) {
      const existingMessages = form.rawForm.values.clientMessages;
      // an omitted key means firm default
      // never send null, the update schema rejects it
      const resolveMessage = (
        next: string | null | undefined,
        existing: string | null | undefined,
      ) => (next === null ? undefined : next || existing || undefined);
      const currentResponse = form.rawForm.values.response || "";
      const nextResponse =
        messages.confirmationMode === "redirect"
          ? currentResponse
          : messages.confirmation || currentResponse;
      const baselineTranslations = JSON.stringify(
        form.rawForm.values.translations || [],
      );

      try {
        const result = await updateForm({
          variables: {
            uri: form.rawForm.uri,
            fields: {
              title: form.formTitle,
              category: form.formCategory,
              response: nextResponse,
              ...(messages.confirmationMode !== undefined
                ? {
                    redirectUrl:
                      messages.confirmationMode === "message"
                        ? null
                        : messages.redirectUrl,
                  }
                : {}),
              clientMessages:
                messages.new !== undefined ||
                messages.reminder !== undefined ||
                existingMessages
                  ? {
                      new: resolveMessage(messages.new, existingMessages?.new),
                      reminder: resolveMessage(
                        messages.reminder,
                        existingMessages?.reminder,
                      ),
                    }
                  : undefined,
            },
          },
        });

        await updateFormPublishStatus({
          variables: { uri: formUri, hasUnpublishedChanges: true },
        });

        if (nextResponse !== currentResponse) {
          startFormTranslationPolling({
            baselineTranslations,
            baselineOperationId:
              result?.data?.updateForm?.values.operationId ??
              form.rawForm.values.operationId,
          });
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const findMatterTypeDifference = ({
    originalMatterTypes,
    modifiedMatterTypes,
  }: {
    originalMatterTypes: GqlMatterType[];
    modifiedMatterTypes: GqlMatterType[];
  }) => {
    const matterTypesToAdd = modifiedMatterTypes.filter(
      (modifiedMatterType) =>
        !originalMatterTypes.some(
          (original) => original.id === modifiedMatterType.id,
        ),
    );

    const matterTypesToRemove = originalMatterTypes.filter(
      (originalMatterType) =>
        !modifiedMatterTypes.some(
          (modified) => modified.id === originalMatterType.id,
        ),
    );

    return { matterTypesToAdd, matterTypesToRemove };
  };

  const updateFormInfo = async (values: EditingFormInfo) => {
    if (!form) return;

    await updateForm({
      variables: {
        uri: form.rawForm.uri,
        fields: {
          title: form.formTitle,
          category: values.formCategory,
          response: form.rawForm.values.response,
        },
      },
    }).catch(console.error);

    await updateFormPublishStatus({
      variables: { uri: formUri, hasUnpublishedChanges: true },
    });

    const { matterTypesToAdd, matterTypesToRemove } = findMatterTypeDifference({
      originalMatterTypes:
        matterTypes?.map((mt) => ({
          category: mt.category,
          id: mt.id,
          location: mt.location,
          name: mt.name,
          representativeOptions: mt.representativeOptions,
          source: mt.source,
        })) || [],
      modifiedMatterTypes: values.matterTypeGroups.flatMap(
        (g) => g.matterTypes,
      ),
    });

    if (!matterTypesToAdd.length && !matterTypesToRemove.length) return;

    await Promise.allSettled(
      matterTypesToRemove.map((matterType) =>
        removeFormMatterType({
          variables: {
            formUri,
            matterType,
          },
        }),
      ),
    ).catch(console.error);

    await Promise.allSettled(
      matterTypesToAdd.map((matterType) =>
        addFormMatterType({
          variables: {
            formUri,
            matterType,
          },
        }),
      ),
    ).catch(console.error);
  };

  const onSaveLanguages = async (languages: LanguageKey[]) => {
    try {
      await translateForm({
        variables: {
          formUri,
          fields: {
            supportedLanguages: languages.map((language) => ({
              key: language,
              label: getLanguageLabel(language, supportedLanguages),
            })),
          },
        },
      });
      await updateFormPublishStatus({
        variables: { uri: formUri, hasUnpublishedChanges: true },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const disabled =
    loadingStatus === "checking" ||
    loadingStatus === "timedOut" ||
    form?.aiUserFeedback?.status === "generating" ||
    form?.aiUserFeedback?.status === "generated" ||
    (loading.form && !formTitle);

  return (
    <PublishProvider>
      <div key={viewingLanguage} className="flex flex-col h-screen">
        <FormHeading
          formUri={formUri}
          formTitle={formTitle || ""}
          tab={activeTab}
          isMatterSpecific={!!form?.rawForm.values.matterId}
          isFormActive={isFormActive}
          setTab={setTab}
          canActivate={canActivate}
          disabled={disabled}
          updating={updatingForm || updatingSlug || translatingForm}
          loading={loading}
          canUpdate={canUpdate}
          matterTypeNames={matterTypeNames || []}
          matterTypeLocations={matterTypeLocations || []}
          onActiveToggleClick={onActiveToggleClick}
          onFormTitleUpdate={updateFormTitle}
          validateFormTitle={validateFormTitle}
          saveMessageTemplates={saveMessageTemplates}
          currentClientMessages={{
            new: form?.rawForm.values.clientMessages?.new,
            reminder: form?.rawForm.values.clientMessages?.reminder,
            confirmation: form?.rawForm.values.response,
          }}
          currentRedirectUrl={form?.rawForm.values.redirectUrl}
          defaultClientMessage={setting?.defaultClientMessage}
          messagePlaceholderData={{
            formTitle: form?.formTitle,
            firmName: team?.values.name,
            firmPhone: team?.values.phone,
            matterCategory: form?.formCategory,
          }}
          responsePlaceholderData={{
            firmName: team?.values.name,
            firmPhone: team?.values.phone,
            matterCategory: form?.formCategory,
            clientFirstName: "",
            clientLastName: "",
          }}
          updateFormInfo={updateFormInfo}
          setting={setting}
          category={form?.formCategory || category}
          matterTypes={matterTypes || []}
          groups={groups}
          fields={fields}
          clearIntakeForm={clearIntakeForm}
          disableTabs={loadingStatus === "checking"}
          creationStatus={form?.creationStatus}
          onSaveLanguages={onSaveLanguages}
          supportedLanguages={supportedLanguages}
          viewingLanguage={viewingLanguage}
          setViewingLanguage={setViewingLanguage}
          hasUnpublishedChanges={
            form?.rawForm.values.hasUnpublishedChanges ?? false
          }
          submissionLinkHashes={submissionLinkHashes}
        />
        <DeactivateFormModal
          open={deactivateConfirmOpen}
          loading={updatingSlug}
          onClose={() => setDeactivateConfirmOpen(false)}
          onConfirm={confirmDeactivateForm}
        />
        <PublishForm formUri={formUri} formTitle={formTitle || ""} />
        <View
          tab={activeTab}
          fields={fields}
          formUri={formUri}
          creationStatus={form?.creationStatus}
          sections={sections}
          team={team}
          setting={setting}
          form={form}
          groups={groups}
          loading={loading}
          previewOptions={{
            previewErrorToggle,
            previewResponses,
            previewLastSection,
          }}
          loadingStatus={loadingStatus}
          matterTypes={matterTypes}
          matterLayouts={matterLayouts}
          loadMatterFields={loadMatterFields}
          createForm={createForm}
          updateAIFillSettings={updateAIFillSettings}
          canUpdate={canUpdate}
          setTab={setTab}
          teamForms={teamForms}
          editFormLastSectionUri={editFormLastSectionUri}
          staffDetails={staffDetails}
          intakeFormKey={intakeFormKey}
          setToasts={setToasts}
          viewingLanguage={viewingLanguage}
          submissionLinkHashes={submissionLinkHashes}
          toggleFormActive={toggleFormActive}
        />
      </div>
    </PublishProvider>
  );
};

export { EditFormV2 };
